
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import axios from "axios";
import { BTable, BFormCheckbox, BCollapse, BButton } from "bootstrap-vue";
import AppConfig from "@/config/AppConfig";
import IWineSensorAttribute, {
  IWineSensorAttributeGroup,
  WineSensorSelections
} from "@/models/IWineSensorAttribute";

interface OptionRowItem {
  attributeName: string;
  isFirstRow: boolean;
  totalRows: number;
  options: any[];
}

@Component({
  components: {
    BTable,
    BFormCheckbox,
    BCollapse,
    BButton
  }
})
export default class WineSensorAttributes extends Vue {
  @Prop({ default: null }) readonly wineTypeId!: number | null;
  @Prop({ default: () => ({}) }) readonly value!: WineSensorSelections;
  @Prop({ default: "" }) readonly backendError!: string;

  protected attributes: IWineSensorAttribute[] = [];
  protected loading = false;
  protected error = "";
  private requestSequence = 0;

  // Reactive state for tracking collapsed groups: { [groupKey]: boolean }
  protected collapsedGroups: Record<string, boolean> = {};

  protected get displayError() {
    return (
      this.error ||
      (this.backendError
        ? "Die sensorischen Eigenschaften konnten nicht gespeichert werden."
        : "")
    );
  }

  // Returns field definitions including column 1 for Attribute Name with dynamic rowspan
  protected getTableFields(attribute: IWineSensorAttribute) {
    const fields = [
      {
        key: "attributeName",
        label: "Kriterien",
        thStyle: { width: "20%" },
        tdAttr: (value: any, key: string, item: OptionRowItem) => {
          if (item.isFirstRow) {
            return { rowspan: item.totalRows, style: "width: 20%;" };
          }
          return { style: "display: none;" };
        }
      },
      { key: "option_0", label: "Stufe 1", thStyle: { width: "16%" } },
      { key: "option_1", label: "Stufe 2", thStyle: { width: "30%" } },
      { key: "option_2", label: "Stufe 3", thStyle: { width: "16%" } },
      { key: "option_3", label: "Stufe 4", thStyle: { width: "16%" } },
      { key: "option_4", label: "Stufe 5", thStyle: { width: "16%" } }
    ];
    return attribute.key == "flavours-red-wine" ? fields.slice(1, 4) : fields;
  }

  @Watch("wineTypeId", { immediate: true })
  protected async loadAttributes(wineTypeId: number | null) {
    const sequence = ++this.requestSequence;
    this.error = "";
    if (!wineTypeId) {
      this.attributes = [];
      this.$emit("input", {});
      return;
    }
    this.loading = true;
    try {
      const response = await axios.get(
        `${AppConfig.ENTRYPOINT}/api/wine-types/${wineTypeId}/sensor-attributes`
      );
      if (sequence !== this.requestSequence) return;
      this.attributes = response.data.data || response.data || [];

      const validIds = this.attributes.map(attribute => attribute.id);
      const next: WineSensorSelections = {};
      Object.keys(this.value).forEach(key => {
        const id = Number(key);
        if (validIds.includes(id) && this.value[id].length)
          next[id] = this.value[id];
      });
      this.$emit("input", next);
      // Initialize collapsible states: Open 1st group, collapse all others
      this.initializeCollapsedState();
    } catch (_) {
      if (sequence !== this.requestSequence) return;
      this.attributes = [];
      this.error =
        "Die sensorischen Eigenschaften konnten nicht geladen werden.";
    } finally {
      if (sequence === this.requestSequence) this.loading = false;
    }
  }
  // Helper method to set up initial group collapse states
  protected initializeCollapsedState(): void {
    const collapsedState: Record<string, boolean> = {};
    let namedGroupsSeen = 0;

    this.attributes.forEach(attribute => {
      if (this.isSlider(attribute)) return;

      const groups = this.optionGroups(attribute);
      groups.forEach(group => {
        // Nameless/ungrouped sets should never be collapsed
        if (!group.name) {
          collapsedState[group.key] = false;
          return;
        }

        // First named group stays open (false), subsequent named groups collapse (true)
        collapsedState[group.key] = namedGroupsSeen > 0;
        namedGroupsSeen++;
      });
    });

    this.collapsedGroups = collapsedState;
  }

  protected isSlider(attribute: IWineSensorAttribute) {
    return (
      attribute.selection_type === "single" &&
      /colour|color|farbe/i.test(
        attribute.key
        // attribute.selection_type === "single" &&
        // /acidity|body|intensity|finish|colour|color|saeure|korper|abgang|farbe/i.test(
        //   attribute.key
      )
    );
  }

  protected isColour(attribute: IWineSensorAttribute) {
    return /colour|color|farbe/i.test(attribute.key);
  }

  protected isSelected(attributeId: number) {
    return Boolean(this.value[attributeId] && this.value[attributeId].length);
  }

  protected hasOption(attributeId: number, optionId: number) {
    return Boolean(this.value[attributeId]?.includes(optionId));
  }

  protected update(attributeId: number, optionIds: number[]) {
    const next = { ...this.value };
    if (optionIds.length) next[attributeId] = optionIds;
    else delete next[attributeId];
    this.$emit("input", next);
  }

  protected clear(attributeId: number) {
    this.update(attributeId, []);
  }

  protected selectSingle(attributeId: number, optionId: number) {
    this.update(attributeId, [optionId]);
  }

  protected toggleSingle(attributeId: number, optionId: number) {
    this.update(
      attributeId,
      this.hasOption(attributeId, optionId) ? [] : [optionId]
    );
  }

  protected toggleMultiple(attributeId: number, optionId: number) {
    const selected = this.value[attributeId] || [];
    this.update(
      attributeId,
      selected.includes(optionId)
        ? selected.filter(id => id !== optionId)
        : [...selected, optionId]
    );
  }

  protected toggleOption(attribute: IWineSensorAttribute, optionId: number) {
    if (attribute.selection_type === "single") {
      this.toggleSingle(attribute.id, optionId);
      return;
    }

    this.toggleMultiple(attribute.id, optionId);
  }

  protected sliderIndex(attribute: IWineSensorAttribute) {
    const selected = this.value[attribute.id]?.[0];
    const index = attribute.options.findIndex(option => option.id === selected);
    return index < 0 ? 0 : index;
  }

  protected selectSlider(attribute: IWineSensorAttribute, event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    const option = attribute.options[index];
    if (option) this.selectSingle(attribute.id, option.id);
  }

  protected optionGroups(
    attribute: IWineSensorAttribute
  ): IWineSensorAttributeGroup[] {
    if (attribute.groups?.length) {
      const groups = [...attribute.groups];
      const groupedOptionIds = new Set(
        groups.reduce<number[]>((ids, group) => {
          return ids.concat(group.options.map(option => option.id));
        }, [])
      );
      const ungroupedOptions = attribute.options.filter(
        option => !groupedOptionIds.has(option.id)
      );

      if (ungroupedOptions.length) {
        groups.push({
          id: 0,
          key: `${attribute.id}_ungrouped`,
          name: "",
          options: ungroupedOptions
        });
      }

      return groups.map(g => ({
        ...g,
        key: `${attribute.id}_${g.key}`
      }));
    }

    const groups: { [key: string]: IWineSensorAttributeGroup } = {};
    attribute.options.forEach(option => {
      const rawKey = option.group ? String(option.group.id) : "ungrouped";
      const uniqueKey = `${attribute.id}_${rawKey}`;

      if (!groups[uniqueKey]) {
        groups[uniqueKey] = {
          id: option.group?.id || 0,
          key: uniqueKey,
          name: option.group?.name || "",
          options: []
        };
      }
      groups[uniqueKey].options.push(option);
    });

    return Object.keys(groups).map(key => groups[key]);
  }

  protected splitOptionLabel(label: string) {
    const match = label.match(/^(.*?)\s*(\([^)]*\))$/);

    return {
      label: match ? match[1].trim() : label,
      detail: match ? match[2] : null
    };
  }

  // Chunks options into 5-item rows & sets rowspan count on row 1 for the attribute column
  protected getOptionTableRows(
    attribute: IWineSensorAttribute,
    options: any[]
  ): OptionRowItem[] {
    const chunkSize = attribute.key !== "flavours-red-wine" ? 5 : 3;
    const rows: OptionRowItem[] = [];
    if (!options || !options.length) return [];

    const totalRows = Math.ceil(options.length / chunkSize);

    for (let i = 0; i < totalRows; i++) {
      rows.push({
        attributeName: attribute.name,
        isFirstRow: i === 0,
        totalRows,
        options: options.slice(i * chunkSize, (i + 1) * chunkSize)
      });
    }

    return rows;
  }

  // Check group collapsed state
  protected isGroupCollapsed(group: IWineSensorAttributeGroup): boolean {
    if (!group.name) return false;
    return Boolean(this.collapsedGroups[group.key]);
  }

  // Toggle group collapse state
  protected toggleGroup(groupKey: string): void {
    this.$set(this.collapsedGroups, groupKey, !this.collapsedGroups[groupKey]);
  }
  // Checks if the current attribute and group represent the first non-slider table rendered
  protected isFirstTableOverall(
    attributeIndex: number,
    groupIndex: number
  ): boolean {
    // Find the index of the first non-slider attribute
    const firstTableAttrIndex = this.attributes.findIndex(
      attr => !this.isSlider(attr)
    );

    return attributeIndex === firstTableAttrIndex && groupIndex === 0;
  }
}
